import React, { useEffect, useState } from "react";
import { Route, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, CircularProgress, Paper, Typography, Button } from "@mui/material";
import api from "../services/api";

/**
 * HqRoute — guarda das rotas /hq/*.
 *
 * Reaproveita o JWT principal do OxeTech (sem login extra). Verifica acesso ao
 * HQ via GET /hq/me — o backend cruza email do aluno logado com `equipe_ponto`
 * e valida `hq_nivel`.
 *
 * - Não logado → redireciona para `/` (login padrão)
 * - Logado mas sem `hq_nivel` → tela "sem acesso ao HQ"
 * - Logado com `hq_nivel` → renderiza componente com `hqMembro` como prop
 */

export const HqMembroContext = React.createContext(null);

export default function HqRoute({ component: Component, ...rest }) {
  const { signed, token } = useSelector((state) => state.auth);
  const location = useLocation();
  const [estado, setEstado] = useState({ loading: true, membro: null, semAcesso: false });

  useEffect(() => {
    if (!signed || !token) {
      setEstado({ loading: false, membro: null, semAcesso: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/api/hq/me", { cache: false });
        // Defesa: se o backend não atende /hq/* (nginx fora do regex), a resposta
        // é o HTML do SPA. Detecta e trata como "sem acesso" + flag de nginx.
        const data = r?.data;
        const ehJsonValido = data && typeof data === "object" && data.id && data.hq_nivel;
        if (!cancelled && ehJsonValido) {
          setEstado({ loading: false, membro: data, semAcesso: false, nginxFora: false });
        } else if (!cancelled) {
          setEstado({ loading: false, membro: null, semAcesso: true, nginxFora: true });
        }
      } catch (err) {
        if (cancelled) return;
        setEstado({ loading: false, membro: null, semAcesso: err?.response?.status === 403, nginxFora: false });
      }
    })();
    return () => { cancelled = true; };
  }, [signed, token]);

  if (!signed) {
    return <Redirect to={{ pathname: "/", state: { from: location } }} />;
  }

  if (estado.loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress sx={{ color: "#F46C0C" }} />
      </Box>
    );
  }

  if (estado.semAcesso) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", p: 3 }}>
        <Paper sx={{ p: 4, maxWidth: 520, textAlign: "center", borderRadius: 3 }}>
          {estado.nginxFora ? (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>OxeTech HQ ainda não disponível</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                A API do HQ não está respondendo. Provavelmente o regex do nginx ainda não inclui <code>/hq/*</code>.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 3, fontFamily: "monospace" }}>
                sudo + reload nginx para liberar /hq/* no proxy
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Sem acesso ao OxeTech HQ</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Você não tem perfil HQ configurado para este email. Se você é membro
                da equipe SECTI, solicite à Direção a configuração do seu perfil.
              </Typography>
            </>
          )}
          <Button variant="outlined" href="/dashboard" sx={{ color: "#F46C0C", borderColor: "#F46C0C" }}>
            Voltar para o Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!estado.membro) {
    // Erro genérico (timeout, etc.) — volta pra root
    return <Redirect to={{ pathname: "/", state: { from: location } }} />;
  }

  return (
    <Route
      {...rest}
      render={(props) => (
        <HqMembroContext.Provider value={estado.membro}>
          <Component {...props} hqMembro={estado.membro} />
        </HqMembroContext.Provider>
      )}
    />
  );
}

export function useHqMembro() {
  return React.useContext(HqMembroContext);
}
